import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
// Table imports removed - now using card/accordion view for better mobile responsiveness
import { Pencil, Trash2, Plus, Save, X, ChevronDown, ChevronUp, Calendar, User, Target, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useTenancy } from '@/contexts/TenancyContext';
import { useMSGReadOnly } from '../../../contexts/ReadOnlyContext';
import { Objective, addObjective, updateObjective, deleteObjective, onObjectivesChange } from '@/services/objectivesService';
import { Timestamp } from 'firebase/firestore';
import { useMSGVersion } from '../../../hooks/useMSGVersion';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { FrameworkCombobox, FrameworksProvider } from '@/components/ui/FrameworkCombobox';
import { RisksCombobox, RisksProvider } from '@/components/ui/RisksCombobox';
import { ControlCombobox, ControlsProvider } from '@/components/ui/ControlCombobox';

type ObjectiveFormData = {
    objectiveText: string;
    description: string;
    measurement: string;
    objectiveTargetDate: string;
    owner?: string;
    frameworks: string[];
    relatedRisks: string[];
    relatedControls: (string | number)[];
};

// Helper function to convert Timestamp to Date if needed
const toDate = (date: Date | Timestamp | string | null | undefined): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    if (typeof date === 'string') return new Date(date);
    if (typeof (date as any).toDate === 'function') return (date as any).toDate();
    return new Date();
};
export function ObjectivesTable() {
    const { userIdToName, activeTenancy } = useTenancy();
    const { isReadOnly } = useMSGReadOnly();
    const { incrementVersion } = useMSGVersion();
    const [objectives, setObjectives] = useState<Objective[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editData, setEditData] = useState<ObjectiveFormData>({
        objectiveText: '',
        description: '',
        measurement: '',
        objectiveTargetDate: format(new Date(), 'yyyy-MM-dd'),
        owner: '',
        frameworks: [],
        relatedRisks: [],
        relatedControls: [],
    });
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [expandedObjectives, setExpandedObjectives] = useState<Set<string>>(new Set());
    const [newObjective, setNewObjective] = useState<ObjectiveFormData>({
        objectiveText: '',
        description: '',
        measurement: '',
        objectiveTargetDate: format(new Date(), 'yyyy-MM-dd'),
        frameworks: [],
        relatedRisks: [],
        relatedControls: [],
    });

    // Deep link: open objective by :id
    const { id: routeObjectiveId } = useParams<{ id?: string }>();
    useEffect(() => {
        const rid = String(routeObjectiveId || '').trim();
        if (!rid || loading) return;
        const match = objectives.find(o => String(o.id) === rid);
        if (match) handleEdit(match);
    }, [routeObjectiveId, loading, objectives]);

    // Set up real-time listener for objectives
    useEffect(() => {
        if (!activeTenancy || !activeTenancy.id || activeTenancy.id === '' || activeTenancy.id === "Loading...") return;
        const unsubscribe = onObjectivesChange(activeTenancy?.id || '', (updatedObjectives) => {
            setObjectives(updatedObjectives);
            setLoading(false);
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [activeTenancy?.id]);

    const handleEdit = (objective: Objective) => {
        setEditingId(objective.id);
        setEditData({
            objectiveText: objective.objectiveText,
            description: (objective as any).description || '',
            measurement: (objective as any).measurement || '',
            objectiveTargetDate: objective.objectiveTargetDate
                ? format(toDate(objective.objectiveTargetDate), 'yyyy-MM-dd')
                : format(new Date(), 'yyyy-MM-dd'),
            owner: objective.owner,
            frameworks: Array.isArray((objective as any).frameworks) ? (objective as any).frameworks : [],
            relatedRisks: Array.isArray((objective as any).relatedRisks) ? (objective as any).relatedRisks : [],
            relatedControls: Array.isArray((objective as any).relatedControls) ? (objective as any).relatedControls : [],
        });
        setIsEditDialogOpen(true);
    };

    const toggleExpanded = (id: string) => {
        setExpandedObjectives(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSave = async (id: string) => {
        try {
            const oldObjective = objectives.find(o => o.id === id) || null;
            const targetDate = editData.objectiveTargetDate
                ? new Date(editData.objectiveTargetDate)
                : new Date();

            const payload = {
                objectiveText: editData.objectiveText,
                description: editData.description,
                measurement: editData.measurement,
                objectiveTargetDate: targetDate,
                owner: editData.owner,
                frameworks: editData.frameworks,
                relatedRisks: editData.relatedRisks,
                relatedControls: editData.relatedControls,
            };

            if (editingId === 'new') {
                await addObjective(
                    activeTenancy?.id || '',
                    payload
                );
                // Version snapshot for create
                await incrementVersion(
                    'objective_create',
                    `Created objective: ${editData.objectiveText}`,
                    {
                        oldData: null,
                        newData: payload
                    }
                );
                toast.success('Objective added successfully');
            } else {
                await updateObjective(
                    activeTenancy?.id || '',
                    id,
                    payload
                );
                // Version snapshot for update
                await incrementVersion(
                    'objective_update',
                    `Updated objective ${id}`,
                    {
                        objectiveId: id,
                        oldData: oldObjective,
                        newData: payload
                    }
                );
                toast.success('Objective updated successfully');
            }
            setEditingId(null);
            setEditData({
                objectiveText: '',
                description: '',
                measurement: '',
                objectiveTargetDate: format(new Date(), 'yyyy-MM-dd'),
                owner: '',
                frameworks: [],
                relatedRisks: [],
                relatedControls: [],
            });
        } catch (error) {
            console.error('Error saving objective:', error);
            toast.error('Failed to save objective');
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setIsEditDialogOpen(false);
        setEditData({
            objectiveText: '',
            description: '',
            measurement: '',
            objectiveTargetDate: format(new Date(), 'yyyy-MM-dd'),
            owner: '',
            frameworks: [],
            relatedRisks: [],
            relatedControls: [],
        });
    };

    const handleSaveFromDialog = async () => {
        if (editingId) {
            await handleSave(editingId);
            setIsEditDialogOpen(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this objective?')) {
            try {
                const oldObjective = objectives.find(o => o.id === id) || null;
                await deleteObjective(activeTenancy?.id || '', id);
                await incrementVersion(
                    'objective_delete',
                    `Deleted objective ${id}`,
                    { objectiveId: id, oldData: oldObjective, newData: null }
                );
                toast.success('Objective deleted successfully');
            } catch (error) {
                console.error('Error deleting objective:', error);
                toast.error('Failed to delete objective');
            }
        }
    };

    const handleAddObjective = async () => {
        try {
            const payload = {
                objectiveText: newObjective.objectiveText,
                description: newObjective.description,
                measurement: newObjective.measurement,
                objectiveTargetDate: new Date(newObjective.objectiveTargetDate),
                frameworks: newObjective.frameworks,
                relatedRisks: newObjective.relatedRisks,
                relatedControls: newObjective.relatedControls,
            };

            await addObjective(
                activeTenancy?.id || '',
                payload
            );
            await incrementVersion(
                'objective_create',
                `Created objective: ${newObjective.objectiveText}`,
                { oldData: null, newData: payload }
            );

            toast.success('Objective added successfully');

            setNewObjective({
                objectiveText: '',
                description: '',
                measurement: '',
                objectiveTargetDate: format(new Date(), 'yyyy-MM-dd'),
                frameworks: [],
                relatedRisks: [],
                relatedControls: [],
            });
            setIsAddDialogOpen(false);
        } catch (error) {
            console.error('Error adding objective:', error);
            toast.error('Failed to add objective');
        }
    };

    if (loading) {
        return <div>Loading objectives...</div>;
    }

    return (
        <FrameworksProvider>
            <RisksProvider>
                <ControlsProvider>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-semibold">Objectives</h2>
                            {!isReadOnly && (
                                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                                    <DialogTrigger asChild>
                                        <Button size="sm">
                                            <Plus className="mr-2 h-4 w-4" /> Add Objective
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="bg-secondary">
                                        <DialogHeader>
                                            <DialogTitle>Add New Objective</DialogTitle>
                                            <DialogDescription>
                                                Enter the details for the new objective.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <div className="grid gap-4 py-4">
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="objectiveText" className="text-right">
                                                    Objective
                                                </Label>
                                                <Textarea
                                                    id="objectiveText"
                                                    value={newObjective.objectiveText}
                                                    onChange={(e) =>
                                                        setNewObjective({ ...newObjective, objectiveText: e.target.value })
                                                    }
                                                    className="col-span-3"
                                                />
                                            </div>

                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="description" className="text-right">
                                                    Description
                                                </Label>
                                                <Textarea
                                                    id="description"
                                                    value={newObjective.description}
                                                    onChange={(e) =>
                                                        setNewObjective({ ...newObjective, description: e.target.value })
                                                    }
                                                    className="col-span-3"
                                                />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="measurement" className="text-right">
                                                    Measurement of Success
                                                </Label>
                                                <Textarea
                                                    id="measurement"
                                                    value={newObjective.measurement}
                                                    onChange={(e) =>
                                                        setNewObjective({ ...newObjective, measurement: e.target.value })
                                                    }
                                                    className="col-span-3"
                                                />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="targetDate" className="text-right">
                                                    Target Date
                                                </Label>
                                                <Input
                                                    id="targetDate"
                                                    type="date"
                                                    value={newObjective.objectiveTargetDate}
                                                    onChange={(e) =>
                                                        setNewObjective({ ...newObjective, objectiveTargetDate: e.target.value })
                                                    }
                                                    className="col-span-3"
                                                />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label className="text-right">
                                                    Framework Tags
                                                </Label>
                                                <div className="col-span-3">
                                                    <FrameworkCombobox
                                                        selected={newObjective.frameworks}
                                                        setSelected={(values) => setNewObjective({ ...newObjective, frameworks: values })}
                                                        placeholder="Select frameworks"
                                                        className="w-full"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label className="text-right">
                                                    Related Risks
                                                </Label>
                                                <div className="col-span-3">
                                                    <RisksCombobox
                                                        selected={newObjective.relatedRisks}
                                                        setSelected={(values) => setNewObjective({ ...newObjective, relatedRisks: values })}
                                                        placeholder="Select related risks"
                                                        className="w-full"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label className="text-right">
                                                    Related Controls
                                                </Label>
                                                <div className="col-span-3">
                                                    <ControlCombobox
                                                        selected={newObjective.relatedControls}
                                                        setSelected={(values) => setNewObjective({ ...newObjective, relatedControls: values })}
                                                        placeholder="Select related controls"
                                                        className="w-full"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                                                Cancel
                                            </Button>
                                            <Button
                                                type="button"
                                                onClick={handleAddObjective}
                                                disabled={!newObjective.objectiveText}
                                            >
                                                Add Objective
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            )}
                        </div>

                        {/* Edit Dialog */}
                        <Dialog open={isEditDialogOpen} onOpenChange={(open) => { if (!open) handleCancelEdit(); }}>
                            <DialogContent className="bg-secondary max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Edit Objective</DialogTitle>
                                    <DialogDescription>
                                        Update the details for this objective.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="grid gap-4 py-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-objectiveText">Objective</Label>
                                        <Textarea
                                            id="edit-objectiveText"
                                            value={editData.objectiveText}
                                            onChange={(e) => setEditData({ ...editData, objectiveText: e.target.value })}
                                            className="w-full"
                                            rows={3}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-description">Description</Label>
                                        <Textarea
                                            id="edit-description"
                                            value={editData.description}
                                            onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                            className="w-full"
                                            rows={3}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-measurement">Measurement of Success</Label>
                                        <Textarea
                                            id="edit-measurement"
                                            value={editData.measurement}
                                            onChange={(e) => setEditData({ ...editData, measurement: e.target.value })}
                                            className="w-full"
                                            rows={3}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="edit-targetDate">Target Date</Label>
                                            <Input
                                                id="edit-targetDate"
                                                type="date"
                                                value={editData.objectiveTargetDate}
                                                onChange={(e) => setEditData({ ...editData, objectiveTargetDate: e.target.value })}
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Owner</Label>
                                            <SingleUserCombobox
                                                selected={editData.owner || ''}
                                                setSelected={(value) => setEditData({ ...editData, owner: value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Framework Tags</Label>
                                        <FrameworkCombobox
                                            selected={editData.frameworks}
                                            setSelected={(values) => setEditData({ ...editData, frameworks: values })}
                                            placeholder="Select frameworks"
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Related Risks</Label>
                                        <RisksCombobox
                                            selected={editData.relatedRisks}
                                            setSelected={(values) => setEditData({ ...editData, relatedRisks: values })}
                                            placeholder="Select related risks"
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Related Controls</Label>
                                        <ControlCombobox
                                            selected={editData.relatedControls}
                                            setSelected={(values) => setEditData({ ...editData, relatedControls: values })}
                                            placeholder="Select related controls"
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={handleCancelEdit}>
                                        Cancel
                                    </Button>
                                    <Button type="button" onClick={handleSaveFromDialog} disabled={!editData.objectiveText}>
                                        Save Changes
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>

                        {/* Responsive Card/Accordion View */}
                        <div className="space-y-3">
                            {objectives.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    No objectives found. Click "Add Objective" to create one.
                                </div>
                            ) : (
                                objectives.map((objective) => {
                                    const isExpanded = expandedObjectives.has(objective.id);
                                    return (
                                        <div
                                            key={objective.id}
                                            className="rounded-lg border border-border bg-card overflow-hidden"
                                        >
                                            {/* Header - Always visible */}
                                            <div
                                                className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                                                onClick={() => toggleExpanded(objective.id)}
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Badge variant="outline" className="text-xs shrink-0">
                                                                OBJ-{objective.id}
                                                            </Badge>
                                                            {objective.objectiveTargetDate && (
                                                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                                    <Calendar className="h-3 w-3" />
                                                                    {format(toDate(objective.objectiveTargetDate), 'MMM d, yyyy')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <h3 className="font-medium text-foreground line-clamp-2">
                                                            {objective.objectiveText}
                                                        </h3>
                                                        {(objective as any).description && (
                                                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                                                {(objective as any).description}
                                                            </p>
                                                        )}
                                                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                                            {objective.owner && (
                                                                <span className="flex items-center gap-1">
                                                                    <User className="h-3 w-3" />
                                                                    {userIdToName[objective.owner] || objective.owner}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {!isReadOnly && (
                                                            <>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8"
                                                                    onClick={(e) => { e.stopPropagation(); handleEdit(objective); }}
                                                                >
                                                                    <Pencil className="h-4 w-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8"
                                                                    onClick={(e) => { e.stopPropagation(); handleDelete(objective.id); }}
                                                                >
                                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                                </Button>
                                                            </>
                                                        )}
                                                        {isExpanded ? (
                                                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                                                        ) : (
                                                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Expanded Content */}
                                            {isExpanded && (
                                                <div className="px-4 pb-4 pt-0 border-t border-border bg-muted/30">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                                                        {/* Measurement of Success */}
                                                        {(objective as any).measurement && (
                                                            <div className="space-y-1">
                                                                <div className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                                                    <Target className="h-3 w-3" />
                                                                    Measurement of Success
                                                                </div>
                                                                <p className="text-sm">{(objective as any).measurement}</p>
                                                            </div>
                                                        )}

                                                        {/* Last Updated */}
                                                        {objective.dateUpdated && (
                                                            <div className="space-y-1">
                                                                <div className="text-xs font-medium text-muted-foreground">
                                                                    Last Updated
                                                                </div>
                                                                <p className="text-sm">
                                                                    {format(toDate(objective.dateUpdated), 'MMM d, yyyy')}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Framework Tags */}
                                                        {Array.isArray((objective as any).frameworks) && (objective as any).frameworks.length > 0 && (
                                                            <div className="space-y-1">
                                                                <div className="text-xs font-medium text-muted-foreground">
                                                                    Framework Tags
                                                                </div>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {(objective as any).frameworks.map((fw: string) => (
                                                                        <Badge key={fw} variant="secondary" className="text-xs">
                                                                            {fw}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Related Risks */}
                                                        {Array.isArray((objective as any).relatedRisks) && (objective as any).relatedRisks.length > 0 && (
                                                            <div className="space-y-1">
                                                                <div className="text-xs font-medium text-muted-foreground">
                                                                    Related Risks
                                                                </div>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {(objective as any).relatedRisks.map((risk: string) => (
                                                                        <Badge key={risk} variant="outline" className="text-xs">
                                                                            {risk}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Related Controls */}
                                                        {Array.isArray((objective as any).relatedControls) && (objective as any).relatedControls.length > 0 && (
                                                            <div className="space-y-1 md:col-span-2">
                                                                <div className="text-xs font-medium text-muted-foreground">
                                                                    Related Controls
                                                                </div>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {(objective as any).relatedControls.map((control: string | number) => (
                                                                        <Badge key={control} variant="outline" className="text-xs">
                                                                            {control}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </ControlsProvider>
            </RisksProvider>
        </FrameworksProvider>
    );
}
